import { Extension } from "@tiptap/core";
import { DOMParser } from "@tiptap/pm/model";
import { EditorState, Plugin, PluginKey, Transaction } from "prosemirror-state";
import { ReplaceStep, ReplaceAroundStep, Step } from "prosemirror-transform";

declare module "@tiptap/core" {
    interface Commands<ReturnType> {
        setContent_force: {
            /**
             * Set the content of the editor, bypassing editability checks
             * params are same as setContent, but bypasses editability checks
             * @param content The new content to set
             * @example editor.commands.setContent_force('<p>Example text</p>')
             */
            setContent_force: (
                content: string | Record<string, any>
            ) => ReturnType;
        };
    }
}

export const EditabilityFilter = Extension.create({
    name: "editabilityFilter",

    addProseMirrorPlugins() {
        // Define helper functions outside the plugin context
        const isAllowedTransaction = (
            step: Step,
            transaction: Transaction,
            state: EditorState
        ): boolean => {
            // Check for typical move patterns
            if (
                step instanceof ReplaceStep ||
                step instanceof ReplaceAroundStep
            ) {
                const metaUiEvent = transaction.getMeta("uiEvent");
                const metaPagination = transaction.getMeta("pagination");
                const metaForce = transaction.getMeta("force");
                const metaHistory = transaction.getMeta("history$");
                const isUndoOrRedo =
                    metaHistory &&
                    ("undo" in metaHistory || "redo" in metaHistory);

                if (metaForce) return true; // force change
                if (isUndoOrRedo) {
                    const isInitialEdit =
                        metaHistory.historyState.done.eventCount === 0;
                    if (isInitialEdit) {
                        return false;
                    }
                    // Allow undo/redo operations
                    return true;
                } // undo/redo action
                if (metaPagination) return true; // its coming from add page node

                // Check for drag and drop
                if (metaUiEvent === "drop") return true;

                // Check for cut/paste operations
                if (metaUiEvent === "paste" && transaction.getMeta("paste"))
                    return true;
            }

            return false;
        };

        return [
            new Plugin({
                key: new PluginKey("editabilityFilter"),
                filterTransaction: (transaction, state) => {
                    if (!transaction.docChanged) return true;

                    let shouldCancel = false;

                    for (const step of transaction.steps) {
                        // Check if this transaction is allowed
                        if (isAllowedTransaction(step, transaction, state)) {
                            continue; // Allow move operations
                        }
                        // For other operations, check editability
                        const stepData = step as unknown as {
                            from: number;
                            to?: number;
                        };
                        if (stepData.from === undefined) continue;

                        const from = stepData.from;
                        const to = stepData.to || from;

                        let hasNonEditableNode = false;
                        try {
                            state.doc.nodesBetween(from, to, (node, pos) => {
                                if (
                                    node.attrs &&
                                    node.attrs.editable === false
                                ) {
                                    hasNonEditableNode = true;
                                }
                            });
                        } catch (error) {
                            console.warn(
                                "Error checking non-editable nodes:",
                                error
                            );
                        }

                        if (hasNonEditableNode) {
                            shouldCancel = true;
                        }
                    }

                    if (shouldCancel) {
                        console.warn(
                            "Transaction cancelled due to non-editable nodes"
                        );
                    }

                    return !shouldCancel;
                },
            }),
        ];
    },

    addCommands() {
        return {
            setContent_force:
                (content: string | Record<string, any>) =>
                ({ tr, dispatch, editor }) => {
                    if (dispatch) {
                        const { schema, doc } = editor.state;

                        let newDoc;
                        if (typeof content === "string") {
                            const parser = DOMParser.fromSchema(schema);
                            const element = document.createElement("div");
                            element.innerHTML = content;
                            newDoc = parser.parse(element);
                        } else {
                            newDoc = schema.nodeFromJSON(content);
                        }

                        const transaction = tr
                            .replaceWith(0, doc.content.size, newDoc)
                            .setMeta("force", true)
                            .setMeta("addToHistory", false) // Prevent history entry
                            .setMeta("preventUpdate", true);

                        dispatch(transaction);
                    }
                    return true;
                },
        };
    },
});
